import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sentence } from '@/types/article';
import { Plus, Edit2, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TranslationEditProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  translatedSentences: Sentence[];
  onTranslatedSentencesChange: (sentences: Sentence[]) => void;
  onSave: () => void;
  isEditingExistingArticle?: boolean;
}

const TranslationEdit = ({
  open,
  onOpenChange,
  translatedSentences,
  onTranslatedSentencesChange,
  onSave,
  isEditingExistingArticle = false,
}: TranslationEditProps) => {
  const { t } = useTranslation();
  const [editingTranslation, setEditingTranslation] = useState<{
    index: number;
    field: 'english' | 'vietnamese';
    value: string;
  } | null>(null);

  const editTranslation = (
    index: number,
    field: 'english' | 'vietnamese',
    value: string
  ) => {
    const updated = [...translatedSentences];
    updated[index][field] = value;
    onTranslatedSentencesChange(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('translationEdit.reviewAndEdit')}</DialogTitle>
          <DialogDescription>
            {t('translationEdit.reviewDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {translatedSentences.map((sentence, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">
                  {t('translationEdit.sentence')} {index + 1}
                </span>
                {index === 0 && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {t('translationEdit.title')}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium">
                    {t('translationEdit.germanOriginal')}
                  </Label>
                  <p className="text-sm bg-gray-50 p-2 rounded border">
                    {sentence.german}
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-medium">
                    {t('translationEdit.english')}
                  </Label>
                  {editingTranslation?.index === index &&
                  editingTranslation.field === 'english' ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editingTranslation.value}
                        onChange={e =>
                          setEditingTranslation({
                            ...editingTranslation,
                            value: e.target.value,
                          })
                        }
                        className="min-h-[60px]"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            editTranslation(
                              index,
                              'english',
                              editingTranslation.value
                            );
                            setEditingTranslation(null);
                          }}
                        >
                          {t('actions.save')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingTranslation(null)}
                        >
                          {t('actions.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm bg-blue-50 p-2 rounded border">
                        {sentence.english}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTranslation({
                            index,
                            field: 'english',
                            value: sentence.english,
                          });
                        }}
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        {t('actions.edit')}
                      </Button>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium">
                    {t('translationEdit.vietnamese')}
                  </Label>
                  {editingTranslation?.index === index &&
                  editingTranslation.field === 'vietnamese' ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editingTranslation.value}
                        onChange={e =>
                          setEditingTranslation({
                            ...editingTranslation,
                            value: e.target.value,
                          })
                        }
                        className="min-h-[60px]"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            editTranslation(
                              index,
                              'vietnamese',
                              editingTranslation.value
                            );
                            setEditingTranslation(null);
                          }}
                        >
                          {t('actions.save')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingTranslation(null)}
                        >
                          {t('actions.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm bg-green-50 p-2 rounded border">
                        {sentence.vietnamese}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTranslation({
                            index,
                            field: 'vietnamese',
                            value: sentence.vietnamese,
                          });
                        }}
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        {t('actions.edit')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={onSave}>
            {isEditingExistingArticle ? (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t('translationEdit.updateArticle')}
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                {t('translationEdit.createArticle')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TranslationEdit;
